import React, { useContext } from 'react'
import Box from '@material-ui/core/Box'
import map from 'lodash/map'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'

import { ActionablesContext } from '../../../../../contexts/ActionablesContext'
import { CardContent, CardHeading, CardFooter } from '../../../../../components/layout/Card'
import { Grid } from '../../../../../components/layout/Grid'
import { CueCard, CueCardItem, CueCardTitle } from '../../../../../components/CueCard'
import { Button } from '../../../../../components/Button'
import { Spotlight } from '../../../../../components/Spotlight'
import { R, sumCurrency } from '../../../../../utils/currency'
import { totalRequirementRange } from '../../../utils'
import { FnaContext } from '../../../context'
import { ASSESSMENT_TYPE } from '../../../reducer'

export const Show = () => {
  const {
    state: {
      fna: {
        [ASSESSMENT_TYPE.LIFE_COVER]: assessment
      },
      onCreateNewScenario
    },
    actions: { goToUpdateForm, goToIndex, goToDelete }
  } = useContext(FnaContext)

  const { fnaReadOnly } = useContext(ActionablesContext)
  const dependents = get(assessment, 'dependents', [])
  const lifeCoverBalance = R(get(assessment, 'balance', 0)).value

  return (
    <>
      <CardContent>
        <Box
          px='3rem'
          my='1rem'
          display='flex'
          justifyContent='space-between'
        >
          <Spotlight
            value={sumCurrency([
              get(assessment, 'total_existing_life_cover', 0),
              get(assessment, 'total_from_selected_risk_products', 0),
            ]).format()}
            description={`Total existing cover`} />
          <Spotlight
            value={`${totalRequirementRange(assessment).riskProfile}*`}
            description={`Total required cover`}
            tooltipProps={{
              width: 450,
              title: (<>
                <p>Your required cover range is {totalRequirementRange(assessment).range}</p>
                <p>The total required cover is calculated based on a combination of everything you have to pay for immediately, such as covering any debts you might have, and things you need to pay over time, such as a monthly payment to a dependent.</p>
                <p>If you've added dependents, it's important to note that we make an investment assumption for when the lump sum is paid out. We assume this lump sum is placed in an investment with a target return of your current risk profile.</p>
                <p style={{ marginBottom: 0 }}>We also include a range calculation, to show what the requirement might be if the money was invested differently, with the lower requirement value of the range assuming a return of CPI + 4%, and the higher requirement value assumes a return of CPI - 2%.</p>
              </>)
            }}
          />
          <Spotlight
            value={R(get(assessment, 'balance', 0)).format()}
            description={lifeCoverBalance < 0 ? 'Shortfall' : 'Surplus'} />
        </Box>
        <Box
          display='flex'
        >
          <Box
            m='1rem'
            width='50%'
          >
            <CueCard>
              <CardHeading>
                <h3>Things to pay for immediately</h3>
              </CardHeading>
              <CardContent>
                <CueCardItem
                  name='Selected liabilities'
                  value={R(get(assessment, 'total_from_selected_liabilities', 0)).format()}
                />
                <CueCardItem
                  name='Additional liabilities'
                  value={R(get(assessment, 'total_additional_liabilities', 0)).format()}
                />
              </CardContent>
            </CueCard>
          </Box>
          <Box
            m='1rem'
            width='50%'
          >
            <CueCard>
              <CardHeading>
                <h3>Existing provisions</h3>
              </CardHeading>
              <CardContent>
                <CueCardItem
                  name='Selected assets'
                  value={R(get(assessment, 'total_from_selected_assets', 0)).format()}
                />
                <CueCardItem
                  name='Additional assets'
                  value={R(get(assessment, 'total_additional_assets', 0)).format()}
                />
                <CueCardItem
                  name='Selected risk products'
                  value={R(get(assessment, 'total_from_selected_risk_products', 0)).format()}
                />
                <CueCardItem
                  name='Existing cover'
                  value={R(get(assessment, 'total_existing_life_cover', 0)).format()}
                />
              </CardContent>
            </CueCard>
          </Box>
        </Box>
        {!isEmpty(dependents) &&
          <Box display="flex">
            <Box
              m='1rem'
              width='100%'
            >
              <CueCard>
                <CardHeading>
                  <h3>Things to pay for over time</h3>
                </CardHeading>
                <CardContent>
                  <Box>
                    <h4>Dependents</h4>
                    <Grid mediumColumns>
                      {map(dependents, (dependent, idx) =>
                        <DependentItem key={idx} dependent={dependent} />
                      )}
                    </Grid>
                  </Box>
                </CardContent>
              </CueCard>
            </Box>
          </Box>
        }
      </CardContent>
      <CardFooter className='gap-4'>
        {!fnaReadOnly &&
          <Button trackAs="Life cover: Delete" tertiary onClick={goToDelete(ASSESSMENT_TYPE.LIFE_COVER)}>Delete assessment</Button>
        }
        <Button secondary onClick={goToIndex}>Go back</Button>
        {!fnaReadOnly &&
          <Button trackAs="Life cover: Retake" onClick={goToUpdateForm(ASSESSMENT_TYPE.LIFE_COVER)} primary>Retake assessment</Button>
        }
        {fnaReadOnly &&
          <Button
            primary
            trackAs="Life cover: Create new scenario"
            onClick={() => onCreateNewScenario()}
          >
            Retake in scenario planning
          </Button>
        }
      </CardFooter>
    </>
  )
}

const DependentItem = ({ dependent }) => {
  const { name, value, time_horizon } = dependent
  return (
    <Box
      m='1rem'
      width='100%'
    >
      <CueCard>
        <CardHeading>
          <CueCardTitle>
            {name}
          </CueCardTitle>
        </CardHeading>
        <CardContent>
          <CueCardItem name='Monthly income' value={R(value).format()} />
          <CueCardItem name='Number of years' value={R(time_horizon).value} />
        </CardContent>
      </CueCard>
    </Box>
  )
}
